/** Query hooks KOVIX.Notify — dữ liệu máy chủ qua TanStack Query; bộ lọc từ search params. Hộp thư/tùy chọn luôn là của người đang đăng nhập. */
import type {
  CampaignStatus,
  DeliveryAttemptStatus,
  NotificationCategory,
  NotificationChannel,
  RuleStatus,
  TemplateStatus,
} from '@kovix/api-client';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';
import { must } from '@/shared/lib/must';

export interface TemplateListParams {
  category?: NotificationCategory | undefined;
  status?: TemplateStatus | undefined;
  channel?: NotificationChannel | undefined;
  includeSystem?: boolean | undefined;
}
export interface RuleListParams {
  eventType?: string | undefined;
  status?: RuleStatus | undefined;
  includeSystem?: boolean | undefined;
}
export interface DeliveryListParams {
  channel?: NotificationChannel | undefined;
  status?: DeliveryAttemptStatus | undefined;
  statuses?: DeliveryAttemptStatus[] | undefined;
  campaignId?: string | undefined;
  since?: string | undefined;
}
export interface InboxParams {
  category?: NotificationCategory | undefined;
  unread?: boolean | undefined;
  archived?: boolean | undefined;
  limit?: number | undefined;
}

export const notifyKeys = {
  all: ['notify'] as const,
  inbox: (p: InboxParams) => [...notifyKeys.all, 'inbox', p] as const,
  preferences: () => [...notifyKeys.all, 'preferences'] as const,
  stats: () => [...notifyKeys.all, 'stats'] as const,
  templates: (p: TemplateListParams) => [...notifyKeys.all, 'templates', p] as const,
  template: (id: string) => [...notifyKeys.all, 'template', id] as const,
  templateTransitions: (id: string) => [...notifyKeys.all, 'template-transitions', id] as const,
  rules: (p: RuleListParams) => [...notifyKeys.all, 'rules', p] as const,
  rule: (id: string) => [...notifyKeys.all, 'rule', id] as const,
  fallback: () => [...notifyKeys.all, 'fallback'] as const,
  channels: () => [...notifyKeys.all, 'channels'] as const,
  deliveries: (p: DeliveryListParams) => [...notifyKeys.all, 'deliveries', p] as const,
  deliveriesOf: (id: string) => [...notifyKeys.all, 'deliveries-of', id] as const,
  campaigns: (p: { status?: CampaignStatus | undefined }) => [...notifyKeys.all, 'campaigns', p] as const,
  campaign: (id: string) => [...notifyKeys.all, 'campaign', id] as const,
};

export const withEtag = <T extends { version: number }>(r: { data: T; etag?: string | null | undefined }) => ({
  ...r.data,
  etag: r.etag ?? `"${r.data.version}"`,
});

export function useInbox(p: InboxParams = {}) {
  return useQuery({
    queryKey: notifyKeys.inbox(p),
    queryFn: ({ signal }) => api.notify.inbox({ limit: 100, ...p }, signal).then((r) => r.data),
    placeholderData: (prev) => prev,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}
export function useMyPreferences() {
  return useQuery({
    queryKey: notifyKeys.preferences(),
    queryFn: ({ signal }) => api.notify.myPreferences(signal).then((r) => r.data),
    staleTime: 30_000,
  });
}
export function useNotifyStats() {
  return useQuery({
    queryKey: notifyKeys.stats(),
    queryFn: ({ signal }) => api.notify.stats(signal).then((r) => r.data),
    staleTime: 30_000,
  });
}
export function useTemplates(p: TemplateListParams = {}) {
  return useQuery({
    queryKey: notifyKeys.templates(p),
    queryFn: ({ signal }) => api.notify.templates(p, signal).then((r) => r.data),
    placeholderData: (prev) => prev,
    staleTime: 10_000,
  });
}
export function useTemplate(id: string | undefined) {
  return useQuery({
    queryKey: notifyKeys.template(id ?? ''),
    queryFn: ({ signal }) => api.notify.template(must(id, 'id'), signal).then(withEtag),
    enabled: !!id,
    staleTime: 5_000,
  });
}
export function useTemplateTransitions(id: string | undefined) {
  return useQuery({
    queryKey: notifyKeys.templateTransitions(id ?? ''),
    queryFn: ({ signal }) => api.notify.templateTransitions(must(id, 'id'), signal).then((r) => r.data),
    enabled: !!id,
    staleTime: 10_000,
  });
}
export function useRules(p: RuleListParams = {}) {
  return useQuery({
    queryKey: notifyKeys.rules(p),
    queryFn: ({ signal }) => api.notify.rules(p, signal).then((r) => r.data),
    placeholderData: (prev) => prev,
    staleTime: 10_000,
  });
}
export function useRule(id: string | undefined) {
  return useQuery({
    queryKey: notifyKeys.rule(id ?? ''),
    queryFn: ({ signal }) => api.notify.rule(must(id, 'id'), signal).then(withEtag),
    enabled: !!id,
    staleTime: 5_000,
  });
}
export function useFallbackRules() {
  return useQuery({
    queryKey: notifyKeys.fallback(),
    queryFn: ({ signal }) => api.notify.fallbackRules(signal).then((r) => r.data),
    staleTime: 30_000,
  });
}
export function useChannels() {
  return useQuery({
    queryKey: notifyKeys.channels(),
    queryFn: ({ signal }) => api.notify.channels(signal).then((r) => r.data),
    staleTime: 30_000,
  });
}
export function useDeliveries(p: DeliveryListParams = {}) {
  return useQuery({
    queryKey: notifyKeys.deliveries(p),
    queryFn: ({ signal }) => api.notify.deliveries({ limit: 300, ...p }, signal).then((r) => r.data),
    placeholderData: (prev) => prev,
    staleTime: 5_000,
  });
}
export function useDeliveriesOf(notificationId: string | undefined) {
  return useQuery({
    queryKey: notifyKeys.deliveriesOf(notificationId ?? ''),
    queryFn: ({ signal }) =>
      api.notify.deliveriesOf(must(notificationId, 'notificationId'), signal).then((r) => r.data),
    enabled: !!notificationId,
    staleTime: 5_000,
  });
}
export function useCampaigns(p: { status?: CampaignStatus | undefined } = {}) {
  return useQuery({
    queryKey: notifyKeys.campaigns(p),
    queryFn: ({ signal }) => api.notify.campaigns(p, signal).then((r) => r.data),
    placeholderData: (prev) => prev,
    staleTime: 10_000,
  });
}
export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: notifyKeys.campaign(id ?? ''),
    queryFn: ({ signal }) => api.notify.campaign(must(id, 'id'), signal).then(withEtag),
    enabled: !!id,
    staleTime: 5_000,
  });
}
/** R6.11 — gửi/giao/mở/click của chiến dịch (click tính một lần mỗi người nhận). */
export function useCampaignStats(id: string | undefined) {
  return useQuery({
    queryKey: [...notifyKeys.all, 'campaign-stats', id ?? ''] as const,
    queryFn: ({ signal }) => api.notify.campaignStats(must(id, 'id'), signal).then((r) => r.data),
    enabled: !!id,
    staleTime: 15_000,
  });
}
/** T9 N16-09 + A12 — nhóm bắt buộc + khả dụng Web Push/Telegram (của máy chủ). */
export function useChannelPolicy() {
  return useQuery({
    queryKey: [...notifyKeys.all, 'channel-policy'] as const,
    queryFn: ({ signal }) => api.notify.channelPolicy(signal).then((r) => r.data),
    staleTime: 300_000,
  });
}
/** T9 A12 — trình duyệt đang nhận Web Push của chính mình (không endpoint/khóa). */
export function useMyPushSubscriptions(enabled = true) {
  return useQuery({
    queryKey: [...notifyKeys.all, 'push-subscriptions'] as const,
    queryFn: ({ signal }) => api.notify.pushSubscriptions(signal).then((r) => r.data),
    enabled,
    staleTime: 30_000,
  });
}
/** T9 A12 — liên kết bot Telegram của nền tảng. */
export function useMyTelegram(enabled = true) {
  return useQuery({
    queryKey: [...notifyKeys.all, 'telegram'] as const,
    queryFn: ({ signal }) => api.notify.telegram(signal).then((r) => r.data),
    enabled,
    staleTime: 10_000,
  });
}
/** T9 A17 (Y14) — danh mục loại sự kiện cho màn Quy tắc. */
export function useEventTypes() {
  return useQuery({
    queryKey: [...notifyKeys.all, 'event-types'] as const,
    queryFn: ({ signal }) => api.notify.eventTypes(signal).then((r) => r.data),
    staleTime: 300_000,
  });
}
/** T9 A14 — quy tắc thiếu mẫu hiệu lực + mẫu chờ duyệt. */
export function useTemplateGaps(enabled = true) {
  return useQuery({
    queryKey: [...notifyKeys.all, 'template-gaps'] as const,
    queryFn: ({ signal }) => api.notify.templateGaps(signal).then((r) => r.data),
    enabled,
    staleTime: 30_000,
  });
}
export function useCampaignTransitions(id: string | undefined) {
  return useQuery({
    queryKey: [...notifyKeys.all, 'campaign-transitions', id ?? ''] as const,
    queryFn: ({ signal }) => api.notify.campaignTransitions(must(id, 'id'), signal).then((r) => r.data),
    enabled: !!id,
    staleTime: 10_000,
  });
}
